import httpService from '@/modules/common/services/httpService';

const baseRoute = '/v3/clients/client_notes';

/**
 * @typedef {Object} NoteData
 * @property {string} uid - Note UID
 * @property {string} business_uid - Business UID
 * @property {string} matter_uid - Matter UID
 * @property {string|null} title - Note title
 * @property {string|null} content - Note content
 * @property {string} staff_uid - Staff UID who created the note
 * @property {string|null} entity_uid - Related entity UID
 * @property {string|null} entity_type - Related entity type
 * @property {string|null} entity_title - Related entity title
 * @property {string|null} summary - AI-generated summary
 * @property {string} summary_status - Summary status (none, in_progress, completed, failed)
 * @property {string} created_at - ISO 8601 timestamp
 * @property {string} updated_at - ISO 8601 timestamp
 * @property {number|null} [summaryExpectedAt] - Unix timestamp when summary is expected (if in_progress)
 */

export default {
  /**
   * Get a note by UID
   * @param {string} noteUid - The unique identifier of the note
   * @returns {Promise<NoteData>} The note data
   */
  async get(noteUid) {
    try {
      const response = await httpService.get(`${baseRoute}/${noteUid}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get note ${noteUid}:`, error);
      throw error;
    }
  },

  /**
   * Create a new note
   * @param {Object} noteData - The note data
   * @param {string} noteData.matter_uid - The matter UID (required)
   * @param {string|null} noteData.title - The note title (optional)
   * @param {string|null} noteData.content - The note content (optional)
   * @param {string} [noteData.entity_uid] - The entity UID (optional)
   * @param {string} [noteData.entity_type] - The entity type (optional)
   * @returns {Promise<NoteData>} The created note data
   */
  async add(noteData) {
    try {
      const response = await httpService.post(baseRoute, noteData);
      
      // Capture AI summary ETA header if present
      const summaryETAString = response.headers?.['Note-Summary-ETA-Seconds'];
      const summaryETA = summaryETAString ? parseFloat(summaryETAString) : null;
      
      return {
        ...response.data.data,
        summaryExpectedAt: summaryETA && summaryETA > 0 ? Date.now() + (summaryETA * 1000) : null,
      };
    } catch (error) {
      console.error('Failed to add note:', error);
      throw error;
    }
  },

  /**
   * Update an existing note
   * @param {string} noteUid - The unique identifier of the note to update
   * @param {Object} noteData - The note data to update
   * @param {string|null} [noteData.title] - The note title (optional)
   * @param {string|null} [noteData.content] - The note content (optional)
   * @returns {Promise<NoteData>} The updated note data
   */
  async update(noteUid, noteData) {
    try {
      const response = await httpService.put(`${baseRoute}/${noteUid}`, noteData);
      
      // Capture AI summary ETA header if present
      const summaryETAString = response.headers?.['Note-Summary-ETA-Seconds'];
      const summaryETA = summaryETAString ? parseFloat(summaryETAString) : null;
      
      return {
        ...response.data.data,
        summaryExpectedAt: summaryETA && summaryETA > 0 ? Date.now() + (summaryETA * 1000) : null,
      };
    } catch (error) {
      console.error(`Failed to update note ${noteUid}:`, error);
      throw error;
    }
  },
};
